import { useState, useEffect } from 'react';
import {
    Modal,
    Typography,
    Autocomplete,
    Button,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Box,
    Snackbar,
    Alert
} from "@mui/material";
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import DeleteIcon from '../assets/images/icons/delete_icon.svg';
import ModalBox from "../themes/overrides/ModalBox";
import TextFieldInput from '../themes/overrides/TextFieldInput';
import { useDispatch, useSelector } from "react-redux";
import { putWorkspace } from '../actions/workspaceAction';
import LoadingOverlay from './LoadingOverlay';
import locales from './locales';
import PropTypes from 'prop-types';

const ManageLocalesModal = ({ open, onClose, selectedType, onSuccess }) => {
    const dispatch = useDispatch();
    const [workspaceLocales, setWorkspaceLocales] = useState([]);
    const [selectedLocales, setSelectedLocales] = useState(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [loading, setLoading] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const DEFAULT_LOCALE = 'en';

    const currentWorkspace = useSelector((state) => state.workspace.workspaceInfo);

    useEffect(() => {
        // fetchlocaleFunction()
        if (selectedType) {
            setWorkspaceLocales(currentWorkspace?.data?.locales)
            console.log(currentWorkspace?.data);
        }
    }, [currentWorkspace, selectedType]);

    // const fetchLocaleCodes = async () => {
    //     try {
    //       const response = await axios.get('https://restcountries.com/v3.1/all');
    //       return response.data.map(country => ({
    //         code: country.cca2.toLowerCase(),
    //         name: country.name.common
    //       }));
    //     } catch (error) {
    //       throw new Error('Failed to fetch locales');
    //     }
    //   };

    // const fetchlocaleFunction = async () => {
    //     try {
    //       setLoading(true);
    //       const locales = await fetchLocaleCodes();
    //       setAvailableLocales(locales);
    //     } catch (error) {
    //       setSnackbarMessage('Failed to fetch locales');
    //       setSnackbarSeverity('error');
    //       setOpenSnackbar(true);
    //     } finally {
    //       setLoading(false);
    //     }
    //   };

    const handleAddLocale = async () => {
        setLoading(true);
        try {
            const exists = workspaceLocales.some(
                locale => locale.code === selectedLocales.code
            );

            if (!exists) {
                const updatedLocales = [selectedLocales.code];
                await handleSaveLocales(updatedLocales);
                setWorkspaceLocales(updatedLocales);
                setSelectedLocales(null);
            }
        } catch (error) {
            console.error('Error adding locale:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveLocale = async (localeToRemove) => {
        setLoading(true);
        try {
            let selectedLocaleToRemove = [localeToRemove]
            const workspaceId = selectedType._id;
            const type = 'removeLocales';
            await dispatch(putWorkspace(workspaceId, selectedLocaleToRemove, type))
                .then(() => {
                    onSuccess?.();
                })
            showSnackbar('Locale removed successfully', 'success');
        } catch (error) {
            showSnackbar('Failed to remove Locale', 'error');
            console.error('Error saving locales:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveLocales = async (locales) => {
        setLoading(true);
        try {
            const workspaceId = selectedType._id;
            const type = 'newLocales';
            await dispatch(putWorkspace(workspaceId, locales, type))
                .then(() => {
                    onSuccess?.();
                })
            showSnackbar('Locales updated successfully', 'success');
        } catch (error) {
            showSnackbar('Failed to add Locales', 'error');
            console.error('Error saving locales:', error);
        } finally {
            setLoading(false);
        }
    };

    const showSnackbar = (message, severity) => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setOpenSnackbar(true);
    };

    return (
        <Modal open={open} onClose={onClose}>
            <ModalBox sx={{ width: '700px', maxWidth: '95%' }}>
                <Typography variant="h2" sx={{ mb: 3 }}>Manage Locales</Typography>

                <Box sx={{ display: isMobile ? 'block' : 'flex', gap: 2, mb: 3 }}>
                    <Autocomplete
                        sx={{ flex: 1 }}
                        options={locales}
                        getOptionLabel={(option) => `${option.name} (${option.code})`}
                        value={selectedLocales}
                        onChange={(event, newValue) => setSelectedLocales(newValue)}
                        noOptionsText="No locale found"
                        renderOption={(props, option) => {
                            const isSelected = workspaceLocales?.includes(option.code);
                            return (
                                <li
                                    {...props}
                                    key={option.code}
                                    style={{
                                        opacity: isSelected ? 0.5 : 1,
                                        pointerEvents: isSelected ? 'none' : 'auto',
                                        cursor: isSelected ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    {`${option.name} (${option.code})`}
                                </li>
                            );
                        }}
                        renderInput={(params) => (
                            <TextFieldInput
                                {...params}
                                label="Search Locales"
                                fullWidth
                                sx={{
                                    '& .MuiInputLabel-root': {
                                        fontSize: '16px',
                                        top: '-0px',
                                        color: '#6B6B6B',
                                    },
                                }}
                            />
                        )}
                    />
                    <Button
                        variant="contained"
                        onClick={handleAddLocale}
                        disabled={!selectedLocales || loading}
                        sx={{ width: isMobile ? '100%' : '120px', mt: isMobile ? 2 : 0, }}
                    >
                        Add Locale
                    </Button>
                </Box>

                <List>
                    <Typography variant="h6" sx={{ mb: 2 }}>Workspace Locales</Typography>
                    {workspaceLocales?.length > 0 ? (
                        workspaceLocales?.map((locale, index) => (
                            <ListItem
                                key={index}
                            >
                                <ListItemText
                                    primary={`${locale} ${locale === DEFAULT_LOCALE ? '(Default)' : ''}`}
                                />
                                <IconButton
                                    size="small"
                                    sx={{
                                        color: '#FF4842',
                                        visibility: locale === DEFAULT_LOCALE ? 'hidden' : 'visible'
                                    }}
                                    onClick={() => handleRemoveLocale(locale)}
                                    disabled={locale === DEFAULT_LOCALE}
                                >
                                    <img src={DeleteIcon} alt="delete" />
                                </IconButton>
                            </ListItem>
                        ))
                    ) : (
                        <Box>
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    color: '#1a1a1a'
                                }}
                            >
                                No saved Locales found
                            </Typography>
                        </Box>
                    )}
                </List>
                <Snackbar
                    open={openSnackbar}
                    autoHideDuration={3000}
                    onClose={() => setOpenSnackbar(false)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert
                        onClose={() => setOpenSnackbar(false)}
                        severity={snackbarSeverity}
                        sx={{ width: '100%' }}
                    >{snackbarMessage}</Alert>

                </Snackbar>
                {loading && <LoadingOverlay />}
            </ModalBox>
        </Modal>
    );
};

ManageLocalesModal.propTypes = {
    open:PropTypes.bool,
    onClose:PropTypes.func, 
    selectedType:PropTypes.any, 
    onSuccess:PropTypes.func
};

export default ManageLocalesModal;